// =============================================================================
// src/data/insights-client.ts — Insights por seção do backend (Camada 3 Claude).
// Lê gold.section_insight via Hasura anon (filtro multi-tenant por x-hasura-client-id),
// materializado pelo asset gold_ai_insight do AtalaIA. Fecha o handoff: as decisões de IA
// das seções deixam de ser estáticas (src/data/section-insights.ts) quando o backend responde.
// =============================================================================

import type { SectionId, SectionInsight } from '../app/types';

interface SectionInsightRow {
  readonly surface_id: string;
  readonly label: string;
  readonly headline: string;
  readonly detail: string;
  readonly confidence: number;
  readonly primary_label: string;
  readonly primary_prompt: string;
  readonly why: string;
}

interface InsightsGraphqlResponse {
  readonly data?: { readonly gold_section_insight?: readonly SectionInsightRow[] };
  readonly errors?: readonly unknown[];
}

const DEFAULT_TIMEOUT_MS = 8000;

const SECTION_INSIGHTS_QUERY = `query SectionInsights($clientId: uuid!) {
  gold_section_insight(where: { client_id: { _eq: $clientId } }) {
    surface_id label headline detail confidence primary_label primary_prompt why
  }
}`;

function toInsight(row: SectionInsightRow): SectionInsight {
  return {
    label: row.label,
    headline: row.headline,
    detail: row.detail,
    confidence: row.confidence,
    primary: row.primary_label,
    primaryPrompt: row.primary_prompt,
    why: row.why,
  };
}

function mapRowsBySurface(
  rows: readonly SectionInsightRow[],
): Partial<Record<SectionId, SectionInsight>> {
  const bySurface: Partial<Record<SectionId, SectionInsight>> = {};
  for (const row of rows) bySurface[row.surface_id as SectionId] = toInsight(row);
  return bySurface;
}

/**
 * Busca insights por seção (gold.section_insight) no Hasura anon, filtrados por tenant.
 * Retorna null em QUALQUER falha/vazio → o chamador mantém o mock estático
 * (src/data/section-insights.ts). Espelha o padrão de copilot-client.ts.
 *
 * @example
 *   const live = await fetchSectionInsights(HASURA_URL, clientId);
 *   const insights = { ...STATIC_INSIGHTS, ...(live ?? {}) };
 */
export async function fetchSectionInsights(
  endpoint: string,
  clientId: string,
  fetchImpl: typeof fetch = fetch,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<Partial<Record<SectionId, SectionInsight>> | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetchImpl(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-hasura-client-id': clientId },
      body: JSON.stringify({ query: SECTION_INSIGHTS_QUERY, variables: { clientId } }),
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const json = (await res.json()) as InsightsGraphqlResponse;
    const rows = json.errors ? [] : json.data?.gold_section_insight ?? [];
    return rows.length ? mapRowsBySurface(rows) : null;
  } catch {
    return null;
    /* v8 ignore next -- artefato v8-to-istanbul: try/catch/finally gera um ramo fantasma no `}` de fechamento do catch (sem caminho de origem — o catch só faz `return null`, nunca lança); comprovadamente inalcançável. cf. copilot-client.ts a 100% */
  } finally {
    clearTimeout(timer);
  }
}
